/**
 * A porta da rua.
 *
 * Porte de `lib/features/entry/presentation/pages/comecar_page.dart` e das
 * duas chaves de `../widgets/chaves_da_casa.dart`.
 *
 * A porta de senha é de uma casa: ela pergunta quem é você aqui dentro. Quem
 * chega do anúncio ainda não tem um aqui dentro, e o servidor, sem cookie
 * nenhum, responde com a casa raiz — que está configurada e trancada. O
 * resultado era um beco: a pessoa caía na porta de uma casa que não é dela,
 * sem "abrir a nossa" e sem "entrar com o código", porque esses dois botões só
 * existem numa casa que ninguém configurou ainda.
 *
 * A saída não é afrouxar aquela regra: ela protege quem já mora numa casa de
 * sair dela sem querer. É ter um endereço próprio para quem está chegando.
 * Chegar aqui é a declaração que faltava, e é por isso que esta tela não
 * pergunta nada sobre a casa raiz — ela nem lê qual casa é esta.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErroAPI, ligacao, traduzir, usarSessao } from '@nosso-sempre/shared';
import './comecar.css';

/** Os dois diálogos de `chaves_da_casa.dart`, e a tela em si. */
type Passo = 'porta' | 'codigoDaCasa' | 'pedirCodigo';

export function Comecar() {
  const navegar = useNavigate();
  const { api } = ligacao();
  const { recarregarPorta, recarregarCasa } = usarSessao();

  const [passo, setPasso] = useState<Passo>('porta');
  const [codigoDaCasa, setCodigoDaCasa] = useState('');
  const [digitado, setDigitado] = useState('');
  /** O que no Flutter descia como `SnackBar`. Sem barra flutuante, fica aqui. */
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /**
   * O `_depois`: a casa deste aparelho mudou. Quem lê o estado da porta é o
   * `refreshEnrollment`, e é ele que adota a casa nova antes de a porta
   * desenhar qualquer nome.
   */
  async function depois() {
    await recarregarPorta();
    await recarregarCasa();
    navegar('/', { replace: true });
  }

  /** `abrirCasaNova`. */
  async function abrirCasa() {
    setOcupado(true);
    setAviso(null);
    try {
      const r = await api.abrirCasa();
      setCodigoDaCasa(r.codigo);
      setPasso('codigoDaCasa');
    } catch (err) {
      setAviso(recado(err, 'Não deu para abrir a casa.'));
    } finally {
      setOcupado(false);
    }
  }

  /** `entrarComCodigo`. Código vazio é o mesmo que cancelar, como lá. */
  async function entrarNaCasa() {
    const codigo = digitado.trim();
    if (!codigo) {
      setPasso('porta');
      return;
    }
    setOcupado(true);
    try {
      await api.entrarNaCasa(codigo);
      setDigitado('');
      await depois();
    } catch (err) {
      setAviso(recado(err, 'Não deu para entrar na casa.'));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <main className="cmc">
      <div className="cmc-meio">
        {passo === 'codigoDaCasa' ? (
          <>
            <h1 className="cmc-dialogo-titulo">{traduzir('Nossa casa está aberta')}</h1>
            <p className="cmc-dialogo-texto">{traduzir('Guarde este código:')}</p>
            <p className="cmc-codigo">{codigoDaCasa}</p>
            <p className="cmc-dialogo-texto">
              {traduzir('É com ele que a outra pessoa entra na mesma casa, do aparelho dela.')}
            </p>
            <button className="cmc-cheio" type="button" onClick={() => void depois()}>
              {traduzir('Entendi')}
            </button>
          </>
        ) : passo === 'pedirCodigo' ? (
          <>
            <h1 className="cmc-dialogo-titulo">{traduzir('Código da casa')}</h1>
            <label className="cmc-rotulo" htmlFor="cmc-codigo">
              {traduzir('Código')}
            </label>
            <input
              id="cmc-codigo"
              className="cmc-campo"
              autoFocus
              value={digitado}
              placeholder={traduzir('O que apareceu no outro aparelho')}
              onChange={(e) => setDigitado(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void entrarNaCasa();
              }}
            />
            {aviso ? (
              <p className="cmc-aviso" role="status">
                {aviso}
              </p>
            ) : null}
            <div className="cmc-acoes">
              <button
                className="cmc-texto"
                type="button"
                onClick={() => {
                  setPasso('porta');
                  setAviso(null);
                }}
              >
                {traduzir('Cancelar')}
              </button>
              <button
                className="cmc-texto"
                type="button"
                disabled={ocupado}
                onClick={() => void entrarNaCasa()}
              >
                {traduzir('Entrar')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="cmc-marca">{traduzir('Nosso Sempre')}</h1>
            <p className="cmc-recado">
              {traduzir(
                'Uma casa é o lugar de vocês aqui dentro. Uma pessoa abre, guarda o código e passa para a outra.',
              )}
            </p>
            {aviso ? (
              <p className="cmc-aviso" role="status">
                {aviso}
              </p>
            ) : null}
            <button
              className="cmc-cheio"
              type="button"
              disabled={ocupado}
              onClick={() => void abrirCasa()}
            >
              {traduzir('Abrir a nossa casa')}
            </button>
            <button
              className="cmc-contorno"
              type="button"
              disabled={ocupado}
              onClick={() => {
                setPasso('pedirCodigo');
                setAviso(null);
              }}
            >
              {traduzir('Já temos uma casa')}
            </button>
            <button className="cmc-volta" type="button" onClick={() => navegar('/')}>
              {traduzir('Voltar para a porta')}
            </button>
          </>
        )}
      </div>
    </main>
  );
}

function recado(err: unknown, padrao: string): string {
  return err instanceof ErroAPI && err.message ? err.message : padrao;
}
